import { useFlag } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'

import { BOOKING_METHODS } from 'features/one_off_modal'
import { useTrack } from 'hooks/analytics_events'
import { useToggleState } from 'hooks/useToggleState'

import { CrmInvitee } from './crm_invitee'
import { EditEmailModal } from './edit_email_modal'
import { InviteeDetails } from './invitee_details'
import { LegacyInvitee } from './legacy_invitee'
import { NoShowBanner } from './no_show_banner'
import { Container, Wrapper } from './styles'

export const Invitee = ({ event, invitee, readonly }) => {
  const { id, cancelled, newSchedulable, policy, confirmed, uuid } = invitee

  const [isOpen, toggle] = useToggleState(false)
  const [noShowClicked, turnOnNoShowClicked] = useBooleanState(false)
  const [isEditEmailModalOpen, openEditEmailModal, closeEditEmailModal] = useBooleanState(false)
  const track = useTrack()
  const sendAnalytics = (action, contactId) => {
    track({
      name: 'Contact Interaction',
      properties: {
        action: action || 'profile_view',
        location: 'meetings_page_right_rail',
        contact_uuid: contactId || invitee.uuid,
      }
    })
  }

  // Guest = no contact id; treat invitee with uuid (e.g. GraphQL) as non-guest so we don't show "Invited as guest" after merge
  const isGuest = !id && !uuid
  const isGroup = event.eventType.kind === 'group'
  const isCancelled = cancelled && !newSchedulable
  const isRescheduled = cancelled && newSchedulable
  const isConfirmed = !cancelled && confirmed
  const isPoll = event.eventType.bookingMethod === BOOKING_METHODS.poll
  const canMarkNoShow = !readonly && !isGuest && event.started && policy?.noShow && !isCancelled && !isRescheduled
  const isEventCancelled = event.cancelled
  const canEditEmail = !readonly && !isGuest && !isEventCancelled && policy?.update
  const canReschedule = !readonly && !isGuest && !isEventCancelled && policy?.reschedule && !isPoll
  const canCancel = !readonly && !isGuest && !isEventCancelled && policy?.cancel
  const isReactiveBlockEnabled = useFlag('trust_and_safety_reactive_block')
  const canBlock = isReactiveBlockEnabled && !readonly && !isGuest && !isEventCancelled && !invitee.blockedByHost
  const canUnblock = isReactiveBlockEnabled && !readonly && !isGuest && !isEventCancelled && invitee.blockedByHost && invitee.blockUuid
  const showNoShowBanner = canMarkNoShow && noShowClicked

  const isCRMContactsPageEnabled = useFlag('crm_contacts_page_to_ai_crm_feature')
  const InviteeComponent = isCRMContactsPageEnabled ? CrmInvitee : LegacyInvitee
  return (
    <>
      <Wrapper data-testid={`invitee-${invitee.uuid}`}>
        <Container>
          <InviteeComponent
            isCancelled={isCancelled}
            isRescheduled={isRescheduled}
            isConfirmed={isConfirmed}
            isGuest={isGuest}
            openEditEmailModal={openEditEmailModal}
            isGroup={isGroup}
            turnOnNoShowClicked={turnOnNoShowClicked}
            canEditEmail={canEditEmail}
            canMarkNoShow={canMarkNoShow}
            event={event}
            invitee={invitee}
            toggle={toggle}
            isOpen={isOpen}
            sendAnalytics={sendAnalytics}
            canReschedule={canReschedule}
            canCancel={canCancel}
            canBlock={canBlock}
            canUnblock={canUnblock}
            uuid={uuid}
          />
        </Container>

        {isOpen && (
          <InviteeDetails
            event={event}
            invitee={invitee}
            openEditEmailModal={openEditEmailModal}
          />
        )}

        {showNoShowBanner && <NoShowBanner />}
      </Wrapper>

      {isEditEmailModalOpen && (
        <EditEmailModal invitee={invitee} onClose={closeEditEmailModal} />
      )}
    </>
  )
}
